(function () {
  'use strict';

  var instances = new WeakMap();

  function ReviewsCarousel(root) {
    this.root = root;
    this.slider = root.querySelector('[data-zk-reviews-slider]');
    this.track = root.querySelector('[data-zk-reviews-track]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-zk-reviews-slide]'));
    this.dots = Array.prototype.slice.call(root.querySelectorAll('[data-zk-reviews-dot]'));
    this.index = 0;
    this.physicalIndex = 0;
    this.cloneCount = 0;
    this.timer = null;
    this.dragging = false;
    this.pointerId = null;
    this.startX = 0;
    this.dragX = 0;
    this.resizeFrame = null;

    if (!this.slider || !this.track || this.slides.length < 2) return;

    this.onPointerDown = this.pointerDown.bind(this);
    this.onPointerMove = this.pointerMove.bind(this);
    this.onPointerUp = this.pointerUp.bind(this);
    this.onTransitionEnd = this.transitionEnd.bind(this);
    this.onResize = this.resize.bind(this);
    this.onVisibility = this.visibilityChange.bind(this);
    this.onKeyDown = this.keyDown.bind(this);

    this.build();
    this.bindEvents();
    this.render(false);
    this.startAutoplay();
    root.setAttribute('data-zk-carousel-ready', 'true');
  }

  ReviewsCarousel.prototype.visibleCount = function () {
    if (window.innerWidth >= 990) return Math.min(3, this.slides.length);
    return 1;
  };

  ReviewsCarousel.prototype.slideStep = function () {
    var slide = this.slides[0];
    if (!slide) return 0;
    var styles = window.getComputedStyle(this.track);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    return slide.getBoundingClientRect().width + gap;
  };

  ReviewsCarousel.prototype.makeClone = function (slide) {
    var clone = slide.cloneNode(true);
    clone.setAttribute('data-zk-review-clone', 'true');
    clone.removeAttribute('data-zk-reviews-slide');
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('id');

    /*
     * Reveal observers only know about the original cards. A cloned reveal
     * node would remain transparent and look like an empty carousel slot.
     */
    clone.removeAttribute('data-zk-reveal');
    clone.classList.add('is-visible');
    Array.prototype.forEach.call(clone.querySelectorAll('[data-zk-reveal]'), function (item) {
      item.removeAttribute('data-zk-reveal');
      item.classList.add('is-visible');
      item.style.removeProperty('--zk-reveal-delay');
    });

    Array.prototype.forEach.call(clone.querySelectorAll('[id]'), function (item) {
      item.removeAttribute('id');
    });
    Array.prototype.forEach.call(clone.querySelectorAll('a, button, input, select, textarea, video, iframe'), function (item) {
      item.setAttribute('tabindex', '-1');
    });
    return clone;
  };

  ReviewsCarousel.prototype.build = function () {
    var self = this;
    Array.prototype.forEach.call(this.track.querySelectorAll('[data-zk-review-clone]'), function (clone) {
      clone.remove();
    });

    this.cloneCount = this.visibleCount();
    var before = this.slides.slice(-this.cloneCount);
    var after = this.slides.slice(0, this.cloneCount);

    before.reverse().forEach(function (slide) {
      self.track.insertBefore(self.makeClone(slide), self.track.firstChild);
    });
    after.forEach(function (slide) {
      self.track.appendChild(self.makeClone(slide));
    });

    this.physicalIndex = this.cloneCount + this.index;
  };

  ReviewsCarousel.prototype.bindEvents = function () {
    var self = this;
    this.slider.addEventListener('pointerdown', this.onPointerDown);
    this.slider.addEventListener('keydown', this.onKeyDown);
    this.slider.addEventListener('dragstart', function (event) { event.preventDefault(); });
    window.addEventListener('pointermove', this.onPointerMove, { passive: false });
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    window.addEventListener('resize', this.onResize, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
    this.track.addEventListener('transitionend', this.onTransitionEnd);

    this.dots.forEach(function (dot, dotIndex) {
      dot.addEventListener('click', function () {
        self.goTo(dotIndex);
        self.restartAutoplay();
      });
    });
  };

  ReviewsCarousel.prototype.pointerDown = function (event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (event.target.closest('[data-zk-reviews-dot]')) return;
    this.dragging = true;
    this.pointerId = event.pointerId;
    this.startX = event.clientX;
    this.dragX = 0;
    this.slider.classList.add('is-dragging');
    this.stopAutoplay();
    try { this.slider.setPointerCapture(event.pointerId); } catch (error) { /* Pointer capture is optional. */ }
  };

  ReviewsCarousel.prototype.pointerMove = function (event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    this.dragX = event.clientX - this.startX;
    if (Math.abs(this.dragX) > 6) event.preventDefault();
    this.track.style.transition = 'none';
    this.track.style.transform = 'translate3d(' + (-(this.physicalIndex * this.slideStep()) + this.dragX) + 'px,0,0)';
  };

  ReviewsCarousel.prototype.pointerUp = function (event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;
    this.dragging = false;
    this.pointerId = null;
    this.slider.classList.remove('is-dragging');
    if (this.dragX <= -30) this.next();
    else if (this.dragX >= 30) this.previous();
    else this.render(true);
    this.dragX = 0;
    this.startAutoplay();
  };

  ReviewsCarousel.prototype.next = function () {
    this.index = (this.index + 1) % this.slides.length;
    this.physicalIndex += 1;
    this.render(true);
  };

  ReviewsCarousel.prototype.previous = function () {
    this.index = (this.index - 1 + this.slides.length) % this.slides.length;
    this.physicalIndex -= 1;
    this.render(true);
  };

  ReviewsCarousel.prototype.goTo = function (index) {
    this.index = ((index % this.slides.length) + this.slides.length) % this.slides.length;
    this.physicalIndex = this.cloneCount + this.index;
    this.render(true);
  };

  ReviewsCarousel.prototype.render = function (animate) {
    var self = this;
    var step = this.slideStep();
    if (!step) return;
    this.track.style.transition = animate ? '' : 'none';
    this.track.style.transform = 'translate3d(-' + (this.physicalIndex * step) + 'px,0,0)';

    this.slides.forEach(function (slide, slideIndex) {
      var offset = (slideIndex - self.index + self.slides.length) % self.slides.length;
      var visible = offset < self.visibleCount();
      slide.classList.toggle('is-visible', visible);
      slide.classList.toggle('is-active', slideIndex === self.index);
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });

    this.dots.forEach(function (dot, dotIndex) {
      var active = dotIndex === self.index;
      dot.classList.toggle('is-active', active);
      if (active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (!animate) requestAnimationFrame(function () { self.track.style.transition = ''; });
  };

  ReviewsCarousel.prototype.transitionEnd = function (event) {
    if (event.target !== this.track || event.propertyName !== 'transform') return;
    if (this.physicalIndex >= this.cloneCount + this.slides.length) {
      this.physicalIndex -= this.slides.length;
      this.render(false);
    } else if (this.physicalIndex < this.cloneCount) {
      this.physicalIndex += this.slides.length;
      this.render(false);
    }
  };

  ReviewsCarousel.prototype.keyDown = function (event) {
    if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); this.restartAutoplay(); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.previous(); this.restartAutoplay(); }
  };

  ReviewsCarousel.prototype.resize = function () {
    var self = this;
    cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(function () {
      self.build();
      self.render(false);
    });
  };

  ReviewsCarousel.prototype.startAutoplay = function () {
    var self = this;
    if (document.hidden || this.dragging || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.stopAutoplay();
    this.timer = window.setInterval(function () { self.next(); }, 4000);
  };

  ReviewsCarousel.prototype.stopAutoplay = function () {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  };

  ReviewsCarousel.prototype.restartAutoplay = function () {
    this.stopAutoplay();
    this.startAutoplay();
  };

  ReviewsCarousel.prototype.visibilityChange = function () {
    if (document.hidden) this.stopAutoplay();
    else this.startAutoplay();
  };

  ReviewsCarousel.prototype.destroy = function () {
    this.stopAutoplay();
    cancelAnimationFrame(this.resizeFrame);
    this.slider.removeEventListener('pointerdown', this.onPointerDown);
    this.slider.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.track.removeEventListener('transitionend', this.onTransitionEnd);
    Array.prototype.forEach.call(this.track.querySelectorAll('[data-zk-review-clone]'), function (clone) { clone.remove(); });
    this.root.removeAttribute('data-zk-carousel-ready');
  };

  function findRoots(scope) {
    var roots = [];
    if (scope.nodeType === 1 && scope.matches('[data-zk-reviews]')) roots.push(scope);
    return roots.concat(Array.prototype.slice.call(scope.querySelectorAll('[data-zk-reviews]')));
  }

  function initialize(scope) {
    findRoots(scope || document).forEach(function (root) {
      if (instances.has(root)) return;
      instances.set(root, new ReviewsCarousel(root));
    });
  }

  function destroy(scope) {
    findRoots(scope).forEach(function (root) {
      var instance = instances.get(root);
      if (!instance) return;
      instance.destroy();
      instances.delete(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initialize(document); });
  else initialize(document);

  document.addEventListener('shopify:section:load', function (event) { initialize(event.target); });
  document.addEventListener('shopify:section:unload', function (event) { destroy(event.target); });
})();
